/**
 * PaymentsEditionController — page /paiements-edition.html (EPIC-042 / 42.5).
 *
 * Saisie manuelle d'une transaction de paiement (§7.I.G), modes create et
 * update. Remplace l'ancienne modal `payment-entry-modal` (D1 du 2026-05-23
 * — cf. sous-EPIC 42.5) au profit d'une page dédiée, conformément à la
 * convention `feedback_no_modal_dedicated_edit_page`.
 *
 * Modes :
 *   - **Create** (défaut) → POST `/v1/payments` avec header
 *     `Idempotency-Key` UUID v4. Redirige sur `/paiements.html` au succès.
 *   - **Update** (`?id=<uuid>`) → GET pour pré-remplir puis PATCH
 *     `/v1/payments/{id}`.
 *
 * Query string :
 *   - `?id=<uuid>` : mode update.
 *   - `?invoiceId=<uuid>` : create avec facture pré-rattachée (lookup
 *     best-effort pour afficher le numéro plutôt que l'UUID).
 *   - `?from=payment&amount=X&date=Y` : pré-remplit montant/date (entrée
 *     depuis `/paiements-en-attente.html` — voie réservée, non câblée en V1).
 *
 * Contraintes UI :
 *   - Source `cash` absente du select (espèces = session caisse §7.J).
 *   - Select provider visible uniquement si source = `other`.
 *   - Autocomplete facture debounce 300 ms, statuts
 *     `draft,issued,sent,partially_paid`.
 *   - 423 (exercice clôturé) → bannière `nm-banner--critical`.
 *   - 422 (validation backend) → `aria-invalid` champ par champ + bannière
 *     générique « Certains champs sont invalides. ».
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import { debounce } from "/js/utils/debounce.js";

const INVOICE_AUTOCOMPLETE_STATUSES = "draft,issued,sent,partially_paid";

export default class PaymentsEditionController extends BaseController {
  static targets = [
    "form",
    "transactionDate",
    "amount",
    "paymentSource",
    "paymentProvider",
    "providerField",
    "externalId",
    "invoiceId",
    "invoiceAutocompleteList",
    "notes",
    "errorBanner",
    "submitBtn",
    "syncedBadge",
    "syncedNote",
    "historySection",
    "historyList",
    "historyEmpty",
  ];

  connect() {
    this._mode = "create";
    this._editedId = null;
    this._selectedInvoiceId = null;
    this._invoiceSearchDebounced = debounce(
      () => this._fetchInvoiceMatches(),
      300,
    );

    this._refreshProviderVisibility();
    this._loadProviders().finally(() => this._applyQueryString());
  }

  disconnect() {
    if (this._invoiceSearchDebounced?.cancel) {
      this._invoiceSearchDebounced.cancel();
    }
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  async _loadProviders() {
    try {
      const resp = await api.paymentProviders.list();
      const providers = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp)
          ? resp
          : [];
      this._renderProviderOptions(providers);
    } catch {
      // En cas d'échec : on garde la seule option « — Aucun / Autre — ».
    }
  }

  _renderProviderOptions(providers) {
    if (!this.hasPaymentProviderTarget) return;
    const select = this.paymentProviderTarget;
    while (select.options.length > 1) select.remove(1);
    providers
      .filter((p) => p && p.isActive !== false && !p.archivedAt)
      .forEach((p) => {
        const opt = document.createElement("option");
        opt.value = p.id;
        opt.textContent = p.label ?? p.code ?? p.id;
        select.appendChild(opt);
      });
  }

  async _applyQueryString() {
    const qs = new URLSearchParams(window.location.search);
    const id = qs.get("id");
    const invoiceId = qs.get("invoiceId");
    const from = qs.get("from");

    if (id) {
      this._mode = "update";
      this._editedId = id;
      await this._loadForUpdate(id);
      return;
    }

    if (invoiceId) {
      this._selectedInvoiceId = invoiceId;
      await this._preloadInvoiceLabel(invoiceId);
    }

    if (from === "payment") {
      const amount = qs.get("amount");
      const date = qs.get("date");
      if (amount && this.hasAmountTarget) this.amountTarget.value = amount;
      if (date && this.hasTransactionDateTarget) {
        this.transactionDateTarget.value = date;
      }
    }

    if (this.hasTransactionDateTarget && !this.transactionDateTarget.value) {
      this.transactionDateTarget.value = new Date().toISOString().slice(0, 10);
    }
  }

  async _preloadInvoiceLabel(invoiceId) {
    if (!this.hasInvoiceIdTarget) return;
    try {
      const inv = await api.getInvoice(invoiceId);
      const label = inv?.number
        ? `${inv.number}${inv.clientName ? ` — ${inv.clientName}` : ""}`
        : invoiceId;
      this.invoiceIdTarget.value = label;
    } catch {
      this.invoiceIdTarget.value = invoiceId;
    }
  }

  async _loadForUpdate(id) {
    try {
      const resp = await api.payments.get(id);
      this._fillFormFromPayment(resp?.data ?? resp);
      await this._loadHistory(id);
    } catch (error) {
      this._handleApiError(error, "Impossible de charger ce paiement.");
    }
  }

  // ---------------------------------------------------------------------------
  // Historique (EPIC-099 / 99.12 — §7.I.G).
  // ---------------------------------------------------------------------------

  async _loadHistory(id) {
    if (!this.hasHistorySectionTarget) return;
    try {
      const resp = await api.payments.history(id);
      const events = Array.isArray(resp?.data) ? resp.data : [];
      this._renderHistory(events);
    } catch {
      // Best-effort : un échec sur la timeline ne doit pas bloquer l'écran
      // d'édition. La section reste cachée.
    }
  }

  _renderHistory(events) {
    if (!this.hasHistorySectionTarget) return;
    this.historySectionTarget.hidden = false;
    if (this.hasHistoryListTarget) {
      this.historyListTarget.replaceChildren();
    }
    if (events.length === 0) {
      if (this.hasHistoryEmptyTarget) this.historyEmptyTarget.hidden = false;
      return;
    }
    if (this.hasHistoryEmptyTarget) this.historyEmptyTarget.hidden = true;
    if (!this.hasHistoryListTarget) return;
    [...events].reverse().forEach((event) => {
      const fragment = this.cloneTemplate("payment-history-item-tpl");
      this.fillSlots(fragment, {
        when: this._formatHistoryWhen(event.happenedAt),
        what: this._formatHistoryWhat(event),
      });
      this.historyListTarget.appendChild(fragment);
    });
  }

  _formatHistoryWhen(isoString) {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return String(isoString);
    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  _formatHistoryWhat(event) {
    const actor = this._formatHistoryActor(event);
    const action = this._formatHistoryAction(event);
    return `${action} ${actor}`.trim();
  }

  _formatHistoryActor(event) {
    if (event.actorKind === "webhook") {
      return "— Synchronisation automatique (Wero)";
    }
    if (event.actorKind === "system") {
      return "— Synchronisation automatique (SumUp)";
    }
    if (event.actorKind === "migration") {
      return "— Migration EPIC-099.12";
    }
    if (event.actorUserId) {
      return `— par utilisateur ${event.actorUserId}`;
    }
    return "";
  }

  _formatHistoryAction(event) {
    switch (event.eventType) {
      case "created":
        return "Créé";
      case "archived":
        return "Archivé";
      case "unarchived":
        return "Désarchivé";
      case "attached":
        return "Rattaché à une facture";
      case "detached":
        return "Détaché de la facture";
      case "counter_entered":
        return "Contre-écriture saisie";
      case "updated": {
        const diffs = this._formatHistoryDiffs(event);
        if (diffs === "") return "Modifié";
        return `Modifié — ${diffs}`;
      }
      default:
        return event.eventType ?? "Évènement";
    }
  }

  _formatHistoryDiffs(event) {
    const before = event.before ?? {};
    const after = event.after ?? {};
    const fields = Array.isArray(event.changedFields)
      ? event.changedFields
      : Object.keys({ ...before, ...after });
    return fields
      .filter((field) => before[field] !== after[field])
      .map((field) => {
        const label = this._historyFieldLabel(field);
        const fromValue = this._historyFieldValue(field, before[field]);
        const toValue = this._historyFieldValue(field, after[field]);
        return `${label} : ${fromValue} → ${toValue}`;
      })
      .join(", ");
  }

  _historyFieldLabel(field) {
    const labels = {
      transactionDate: "Date",
      amount: "Montant",
      paymentSource: "Source",
      organizationPaymentProviderId: "Prestataire",
      externalId: "Réf. externe",
      invoiceId: "Facture",
      clientName: "Client",
      notes: "Notes",
      status: "Statut",
    };
    return labels[field] ?? field;
  }

  _historyFieldValue(field, raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    if (field === "amount") {
      const num = Number(raw);
      if (!Number.isNaN(num)) {
        return num.toLocaleString("fr-FR", {
          style: "currency",
          currency: "EUR",
        });
      }
    }
    if (field === "transactionDate") {
      const date = new Date(raw);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString("fr-FR");
      }
    }
    return String(raw);
  }

  _fillFormFromPayment(p) {
    if (!p) return;
    if (this.hasTransactionDateTarget && p.transactionDate) {
      this.transactionDateTarget.value = String(p.transactionDate).slice(0, 10);
    }
    if (this.hasAmountTarget && p.amount != null) {
      this.amountTarget.value = p.amount;
    }
    if (this.hasPaymentSourceTarget && p.paymentSource) {
      this.paymentSourceTarget.value = p.paymentSource;
    }
    if (this.hasPaymentProviderTarget && p.organizationPaymentProviderId) {
      this.paymentProviderTarget.value = p.organizationPaymentProviderId;
    }
    if (this.hasExternalIdTarget && p.externalId) {
      this.externalIdTarget.value = p.externalId;
    }
    if (this.hasNotesTarget && p.notes) {
      this.notesTarget.value = p.notes;
    }
    if (p.invoiceId) {
      this._selectedInvoiceId = p.invoiceId;
      if (this.hasInvoiceIdTarget) {
        this.invoiceIdTarget.value = p.invoiceNumber ?? p.invoiceId;
      }
    }
    this._refreshProviderVisibility();
    if (p.acquisition === "api") {
      this._lockSyncedFields();
    }
  }

  /**
   * Verrou de champ §7.I.D / §7.I.K (EPIC-099.51) : une transaction
   * `acquisition='api'` (ingestion automatique Wero / connecteur PSP) a ses
   * faits monétaires autoritaires. On les rend en lecture seule à l'écran
   * (le serveur les ignore de toute façon) et on révèle le badge. Seuls la
   * facture rattachée et les notes restent éditables.
   */
  _lockSyncedFields() {
    if (this.hasTransactionDateTarget) {
      this.transactionDateTarget.readOnly = true;
    }
    if (this.hasAmountTarget) this.amountTarget.readOnly = true;
    if (this.hasExternalIdTarget) this.externalIdTarget.readOnly = true;
    if (this.hasPaymentSourceTarget) this.paymentSourceTarget.disabled = true;
    if (this.hasPaymentProviderTarget) {
      this.paymentProviderTarget.disabled = true;
    }
    if (this.hasSyncedBadgeTarget) this.syncedBadgeTarget.hidden = false;
    if (this.hasSyncedNoteTarget) this.syncedNoteTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Source select → toggle provider
  // ---------------------------------------------------------------------------

  sourceChanged() {
    this._refreshProviderVisibility();
  }

  _refreshProviderVisibility() {
    if (!this.hasProviderFieldTarget || !this.hasPaymentSourceTarget) return;
    const isOther = this.paymentSourceTarget.value === "other";
    this.providerFieldTarget.hidden = !isOther;
    if (!isOther && this.hasPaymentProviderTarget) {
      this.paymentProviderTarget.value = "";
    }
  }

  // ---------------------------------------------------------------------------
  // Autocomplete facture
  // ---------------------------------------------------------------------------

  invoiceSearchInput() {
    // Toute saisie clavier invalide une éventuelle sélection antérieure :
    // on retombe en mode « pas de facture choisie » jusqu'à validation.
    this._selectedInvoiceId = null;
    this._invoiceSearchDebounced();
  }

  async _fetchInvoiceMatches() {
    if (!this.hasInvoiceIdTarget) return;
    const query = (this.invoiceIdTarget.value || "").trim();
    if (query.length < 2) {
      this._renderInvoiceList([]);
      return;
    }
    try {
      const resp = await api.listInvoices({
        search: query,
        statuses: INVOICE_AUTOCOMPLETE_STATUSES,
      });
      const list = Array.isArray(resp?.data) ? resp.data : [];
      this._renderInvoiceList(list);
    } catch {
      this._renderInvoiceList([]);
    }
  }

  _renderInvoiceList(items) {
    if (!this.hasInvoiceAutocompleteListTarget) return;
    const list = this.invoiceAutocompleteListTarget;
    while (list.firstChild) list.removeChild(list.firstChild);
    items.slice(0, 10).forEach((inv) => {
      const fragment = this.cloneTemplate("invoice-autocomplete-item-tpl");
      if (!fragment) return;
      const label = inv.number
        ? `${inv.number}${inv.clientName ? ` — ${inv.clientName}` : ""}`
        : inv.id;
      this.fillSlots(fragment, { invoiceLabel: label });
      const li = fragment.querySelector("li");
      if (li) li.dataset.invoiceId = inv.id;
      list.appendChild(fragment);
    });
    list.hidden = items.length === 0;
  }

  selectInvoice(event) {
    const li = event.target.closest("li[data-invoice-id]");
    if (!li) return;
    this._selectedInvoiceId = li.dataset.invoiceId;
    if (this.hasInvoiceIdTarget) {
      this.invoiceIdTarget.value = li.textContent.trim();
    }
    if (this.hasInvoiceAutocompleteListTarget) {
      this.invoiceAutocompleteListTarget.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    if (event) event.preventDefault();
    this._hideErrorBanner();
    this._clearFieldErrors();

    const payload = this._collectPayload();
    const clientError = this._validateClientSide(payload);
    if (clientError) {
      this._showErrorBanner(clientError);
      return;
    }

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
    try {
      if (this._mode === "update" && this._editedId) {
        await api.payments.update(this._editedId, payload);
      } else {
        const idempotencyKey = this._newIdempotencyKey();
        await api.payments.create(payload, idempotencyKey);
      }
      window.location.assign("/paiements.html");
    } catch (error) {
      this._handleApiError(error);
    } finally {
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
    }
  }

  _collectPayload() {
    return {
      transactionDate: this.hasTransactionDateTarget
        ? this.transactionDateTarget.value
        : null,
      amount: this.hasAmountTarget ? Number(this.amountTarget.value) : null,
      paymentSource: this.hasPaymentSourceTarget
        ? this.paymentSourceTarget.value
        : null,
      organizationPaymentProviderId:
        this.hasPaymentProviderTarget && this.paymentProviderTarget.value
          ? this.paymentProviderTarget.value
          : null,
      externalId: this.hasExternalIdTarget
        ? this.externalIdTarget.value || null
        : null,
      invoiceId: this._selectedInvoiceId,
      notes: this.hasNotesTarget ? this.notesTarget.value || null : null,
    };
  }

  _validateClientSide(p) {
    if (!p.transactionDate) return "Date d'encaissement requise.";
    const today = new Date().toISOString().slice(0, 10);
    if (p.transactionDate > today) {
      return "La date d'encaissement ne peut pas être dans le futur.";
    }
    if (!p.amount || p.amount <= 0) {
      return "Le montant doit être supérieur à zéro.";
    }
    if (!p.paymentSource) return "Source requise.";
    if (p.paymentSource === "cash") {
      return "Les espèces s'enregistrent uniquement depuis la caisse.";
    }
    return null;
  }

  _handleApiError(error, fallback) {
    if (error instanceof APIError) {
      if (error.status === 423) {
        this._showErrorBanner(
          error.message || "L'exercice est clôturé au livre des recettes.",
        );
        return;
      }
      if (error.status === 422) {
        const fields = error.details?.fields;
        if (Array.isArray(fields) && fields.length > 0) {
          this._applyFieldErrors(fields);
          return;
        }
      }
      this._showErrorBanner(
        error.message || fallback || "Erreur — impossible d'enregistrer.",
      );
      return;
    }
    this._showErrorBanner(
      fallback || "Erreur réseau — impossible d'enregistrer.",
    );
  }

  _applyFieldErrors(fields) {
    fields.forEach((f) => {
      if (!f?.field) return;
      const target = this.element.querySelector(
        `[name="${f.field}"], #${f.field}`,
      );
      if (target) target.setAttribute("aria-invalid", "true");
    });
    this._showErrorBanner("Certains champs sont invalides.");
  }

  _clearFieldErrors() {
    this.element.querySelectorAll("[aria-invalid='true']").forEach((el) => {
      el.removeAttribute("aria-invalid");
    });
  }

  _showErrorBanner(message) {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.hidden = true;
    this.errorBannerTarget.textContent = "";
  }

  _newIdempotencyKey() {
    return typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
